// client/src/features/quotations/RfqUnitMismatchDialog.tsx
//
// Owner decision 2026-09-24: shown when "Send quote" is pressed and at least
// one line is quoted in a different unit than the RFQ asked for
// (`findRfqUnitMismatch` in ./rfqUnitMismatch, the one owner of that
// comparison). NEVER blocks — "Send anyway" continues the send exactly as if
// this dialog had never appeared; "Go fix" only closes it and points at the
// next offending line (each press steps on, D101.3), it does not edit anything itself.
//
// Standalone, not `ConfirmProvider` / `ActionDialog`: a parallel change is
// extending `ConfirmProvider` to take a ReactNode body, so this borrows only
// `useDialogDismiss` (Escape / backdrop-click — the one owner every other
// dialog in the app shares) and the house `Button`, and renders its own
// markup rather than routing through either of those in-flight files.

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDialogDismiss } from '../../components/ui/useDialogDismiss';
import Button from '../../components/ui/Button';
import { describeQuotedAmount, nextMismatchLine, type RfqUnitMismatchEntry } from './rfqUnitMismatch';
import type { ReviewEntry } from './lineMatchReview';

export type RfqUnitMismatchDialogProps = {
  isOpen: boolean;
  /** The mismatched lines, in line-number order — empty while `isOpen` is false. */
  entries: RfqUnitMismatchEntry[];
  /** The line "Go fix" last went to (D101.3), so the button can say which one is next. */
  lastGoneTo?: number | null;
  /**
   * D99 item 4: lines whose catalogue match nobody confirmed (or that are not in the catalogue).
   * Shown in the SAME dialog as the unit mismatches, so Send never asks twice. Advisory only.
   */
  reviewEntries?: ReviewEntry[];
  /** Unit mismatches: closes and points at the next one (`nextMismatchLine`). Review lines only: the first one. */
  onGoFix: () => void;
  /** Continues the send exactly as it would have run with no mismatches. */
  onSendAnyway: () => void;
  onClose: () => void;
};

export const RfqUnitMismatchDialog: React.FC<RfqUnitMismatchDialogProps> = ({
  isOpen,
  entries,
  lastGoneTo = null,
  reviewEntries = [],
  onGoFix,
  onSendAnyway,
  onClose,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const dismiss = useDialogDismiss(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  if (!isOpen) return null;

  const nextLine = nextMismatchLine(entries, lastGoneTo);
  const position = entries.findIndex((e) => e.lineNumber === nextLine) + 1;

  return createPortal(
    <div
      {...dismiss}
      className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-900/25 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-label="Lines to check before sending"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl outline-none flex flex-col max-h-[85vh]"
      >
        <div className="h-1 w-full shrink-0 bg-amber-500" />

        <div className="flex items-start gap-3 px-5 pt-4 pb-3 shrink-0">
          <div
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 text-sm font-bold"
            aria-hidden="true"
          >
            !
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-900">
              {entries.length > 0 && reviewEntries.length > 0
                ? 'Some lines need a look before sending'
                : entries.length > 0
                  ? `${entries.length} line${entries.length === 1 ? '' : 's'} not in the unit the RFQ asked for`
                  : `${reviewEntries.length} line${reviewEntries.length === 1 ? '' : 's'} with a catalogue match nobody has confirmed`}
            </h3>
            <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
              {entries.length > 0 ? 'Sending in the unit the vessel asked for is the safer read. ' : ''}Nothing here is required —
              fix the lines below, or send them exactly as they are.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="-mr-1 -mt-1 h-7 w-7 shrink-0 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <span className="text-lg leading-none">&times;</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-100 px-5 py-3">
          {entries.length > 0 && reviewEntries.length > 0 && (
            <p className="mb-1 text-xs font-semibold text-slate-700">Not in the unit the RFQ asked for</p>
          )}
          <ul className="space-y-1.5">
            {entries.map((entry) => (
              <li
                key={entry.lineNumber}
                className="flex items-start gap-2 rounded-md border border-slate-200 bg-amber-50/60 px-2.5 py-1.5"
              >
                <span className="mt-px shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                  Line {entry.lineNumber}
                </span>
                <span className="min-w-0 text-[12.5px] leading-snug text-slate-700">
                  {entry.itemName || 'This item'} — RFQ asked {entry.askedQty} {entry.askedUom}, {describeQuotedAmount(entry)}.
                </span>
              </li>
            ))}
          </ul>
          {reviewEntries.length > 0 && (
            <>
              {entries.length > 0 && <p className="mb-1 mt-3 text-xs font-semibold text-slate-700">Catalogue match not confirmed</p>}
              <ul className="space-y-1.5" data-testid="review-entries">
                {reviewEntries.map((entry) => (
                  <li key={entry.lineNumber} className="flex items-start gap-2 rounded-md border border-slate-200 bg-amber-50/60 px-2.5 py-1.5">
                    <span className="mt-px shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                      Line {entry.lineNumber}
                    </span>
                    <span className="min-w-0 text-[12.5px] leading-snug text-slate-700">
                      {entry.itemName || 'This item'} — {entry.reasons.join('; ')}.
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3 shrink-0">
          <Button variant="secondary" onClick={onGoFix}>
            {entries.length > 1 && position > 0
              ? `Go fix (${position} of ${entries.length})`
              : entries.length > 0 ? 'Go fix' : 'Go back'}
          </Button>
          <Button variant="primary" onClick={onSendAnyway}>
            Send anyway
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RfqUnitMismatchDialog;
